// src/pages/markets/board/api/useEventScores.ts

import { useRef } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { boardKeys } from './boardKeys';
import { getScoreBatcher, type ScoreEntry } from './scoreBatcher';
import type { BoardScore, EventConvictionIndex } from './boardTypes';

const SCORE_STALE_MS = 55_000;
const SCORE_GC_MS = 10 * 60_000;

export interface EventScores {
    scores: BoardScore[];
    index: EventConvictionIndex | undefined;
    isFetching: boolean;
    error: Error | null;
}

function mergeEntries(eventId: string, parts: (ScoreEntry | null)[]): {
    scores: BoardScore[];
    index: EventConvictionIndex | undefined;
} {
    const scores: BoardScore[] = [];
    const byGroup: EventConvictionIndex['by_group'] = [];
    const primary: Record<string, string> = {};
    let latest = '';
    let any = false;

    for (const p of parts) {
        if (!p) continue;
        scores.push(...p.scores);
        if (p.index) {
            any = true;
            byGroup.push(...p.index.by_group);
            Object.assign(primary, p.index.primary_market_by_group);
            if (p.index.latest_ts > latest) latest = p.index.latest_ts;
        }
    }

    if (!any) return { scores, index: undefined };
    return {
        scores,
        index: {
            event_id: eventId,
            score_count: scores.length,
            markets_with_scores: new Set(scores.map(s => s.market_id)).size,
            latest_ts: latest,
            by_group: byGroup,
            primary_market_by_group: primary,
        },
    };
}

export function useEventScores(eventId: string, groups: readonly string[]): EventScores {
    const qc = useQueryClient();

    const results = useQueries({
        queries: groups.map(g => ({
            queryKey: boardKeys.score(eventId, g),
            queryFn: () => getScoreBatcher(qc).load(eventId, g),
            staleTime: SCORE_STALE_MS,
            gcTime: SCORE_GC_MS,
        })),
    });

    const parts = results.map(r => (r.data as ScoreEntry | undefined) ?? null);
    const isFetching = results.some(r => r.isFetching);
    const error = (results.find(r => r.error)?.error as Error | undefined) ?? null;

    // Only rebuild the merged arrays when an entry reference changed; otherwise
    // memo on the card breaks on every render.
    const cache = useRef<{ eventId: string; parts: (ScoreEntry | null)[]; merged: ReturnType<typeof mergeEntries> } | null>(null);
    const prev = cache.current;
    const same =
        prev !== null &&
        prev.eventId === eventId &&
        prev.parts.length === parts.length &&
        prev.parts.every((p, i) => p === parts[i]);
    const merged = same ? prev!.merged : mergeEntries(eventId, parts);
    if (!same) cache.current = { eventId, parts, merged };

    return { scores: merged.scores, index: merged.index, isFetching, error };
}